import moment from "moment";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Tooltip } from "@mui/material";
import { Card, CardBody } from "reactstrap";
import { useHistory, useLocation } from "react-router";

import { ReactComponent as BackIcon } from "assets/img/svg/BackButton.svg";
import { ReactComponent as ExcellIcon } from "assets/img/svg/excel.svg";
import { ReactComponent as EditIcon } from "assets/img/svg/EditGryIcon.svg";

import { maskPhoneNumber } from "../../unAssigned/collectPayment/selectPaymentMode/paymentLink/utils";
import { EyeIcon } from "../studentFinancials/paymentCollection/manage-receipts/EyeIcon";
import { checkPermission } from "../../common/utils/methods/commonMethods/utilityMethod";
import { LastUpdatedDetails } from "../../employeeManagement/userProfile/helper";
import ServerDataGrid from "views/pages/manage/common/data-grid/ServerDataGrid";
import AButton from "../../common/form-fields-mui/AButton";

// add vendor components
import AddVendor from "modules/addVendor/AddVendor";

import {
  VENDOR_LIST_INITIAL_FILTERS,
  searchVendors,
  downloadVendorsCsv,
  vendorListSearchByOptions,
  BUPopover,
  VENDOR_TYPE_ENUM,
  ACTIONABLE_STATUSES,
} from "./helper";
import ListFilters from "./ListFilters";
import ALoader from "../../common/a-loader";
import AInput from "../../common/formFeilds/customInput/AInput";
import { useGetPermissions } from "layouts/customHook";
import { STATUS_BADGE } from "./creation-approval/helper";

const VendorsList = () => {
  const history = useHistory();
  const location = useLocation();
  const firstRender = useRef(true);

  const [loading, setLoading] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const [vendorList, setVendorList] = useState([]);
  const [pageSize, setPageSize] = useState(50);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  const [filters, setFilters] = useState(VENDOR_LIST_INITIAL_FILTERS);
  const [userSearchInput, setUserSearchInput] = useState("");
  const [selected, setSelected] = useState("vendorName");
  const [showAddVendor, setShowAddVendor] = useState(false);
  const userPermissions = useGetPermissions("vendorDashboard");
  const isCreatePermission = checkPermission(userPermissions, "C");

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const filtersParam = query.get("filters");

    if (filtersParam) {
      try {
        const parsedFilters = JSON.parse(filtersParam);

        setUserSearchInput(parsedFilters.singleSearchValue || "");
        setSelected(parsedFilters.singleSearchType || "vendorName");
        setFilters({
          ...VENDOR_LIST_INITIAL_FILTERS,
          vendorType:
            parsedFilters.vendorType || VENDOR_LIST_INITIAL_FILTERS.vendorType,
          businessAreaIds:
            parsedFilters.bu || VENDOR_LIST_INITIAL_FILTERS.businessAreaIds,
          status:
            parsedFilters.accountStatus || VENDOR_LIST_INITIAL_FILTERS.status,
          searchBy: parsedFilters.singleSearchType || "vendorName",
          searchByValue: parsedFilters.singleSearchValue || "",
        });
      } catch (error) {
        console.error("Error parsing filters from query params:", error);
      }
    } else
      fetchVendorList({
        ...filters,
        searchBy: selected,
        searchByValue: userSearchInput,
      });
  }, [location.search]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    } //skip first render

    fetchVendorList({
      ...filters,
      searchBy: selected,
      searchByValue: userSearchInput,
    });
  }, [filters, currentPage, pageSize]);

  const fetchVendorList = async ({ request = filters, page = currentPage }) => {
    setLoading(true);
    try {
      const response = await searchVendors({
        filters: request,
        offset: page,
        limit: pageSize,
      });

      const list = response?.data?.vendorDataList || [];
      const totalCount = response?.data?.totalRecords || 0;

      setVendorList(Array.isArray(list) ? list : []);
      setTotalRows(totalCount);
    } catch (error) {
      console.error("Error fetching vendor list:", error);
      setVendorList([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setLoading(true);
    try {
      await downloadVendorsCsv(filters);
    } catch (error) {
      console.error("Error during CSV export:", error);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDownHandler = (e) => {
    if (e.key === "Enter" || e.keyCode === 13) {
      searchInputHandler();
    }
  };

  const handleOnUserInput = (value) => {
    setUserSearchInput(value);
    if (value === "" && value !== filters.searchByValue)
      searchInputHandler(value);
  };

  const searchInputHandler = (value = userSearchInput) => {
    const updatedFilters = {
      ...filters,
      searchBy: value ? selected : "",
      searchByValue: value,
    };
    setFilters(updatedFilters);
  };

  const columns = useMemo(() => [
    {
      key: "vendorSapCode",
      label: "SAP Vendor ID",
      width: 105,
      sortable: true,
      render: (value) => {
        return value ? <span>{value}</span> : <span>-</span>;
      },
    },
    {
      key: "vendorType",
      label: "Vendor Type",
      width: 150,
      sortable: true,
      render: (value) => {
        return value ? (
          <Tooltip title={value}>{VENDOR_TYPE_ENUM[value]}</Tooltip>
        ) : (
          <span>-</span>
        );
      },
    },
    {
      key: "panNumber",
      label: "PAN",
      width: 120,
      sortable: true,
      render: (value) => {
        return value ? (
          <Tooltip title={value}>
            <div className="ellipsis-text-container">{value}</div>
          </Tooltip>
        ) : (
          <span>-</span>
        );
      },
    },
    {
      key: "vendorName",
      label: "Name",
      width: 100,
      sortable: true,
      render: (value) => (
        <Tooltip title={value}>
          <div className="ellipsis-text-container">{value || "-"}</div>
        </Tooltip>
      ),
    },
    {
      key: "businessAreaValue",
      label: "BU Code",
      width: 135,
      sortable: true,
      render: (value, row) => {
        return value?.trim() ? (
          <div>
            <span>{value}</span>&nbsp;
            <BUPopover buId={row.businessAreaId} />
          </div>
        ) : (
          <span>All</span>
        );
      },
    },
    {
      key: "sapPlantId",
      label: "Plant ID",
      width: 130,
      sortable: true,
      render: (value) => {
        return value ? (
          <Tooltip title={value}>
            <span>{value}</span>
          </Tooltip>
        ) : (
          <span>-</span>
        );
      },
    },
    {
      key: "contactNumber",
      label: "Phone No",
      width: 200,
      sortable: true,
      render: (value) => {
        return value ? (
          <div className="ellipsis-text-container">
            {maskPhoneNumber(value)}
          </div>
        ) : (
          <span>-</span>
        );
      },
    },
    {
      key: "occupation",
      label: "Job Details",
      width: 100,
      sortable: true,
      render: (value) => (
        <Tooltip title={value}>
          <span>{value || "-"}</span>
        </Tooltip>
      ),
    },
    {
      key: "onboardingDate",
      label: "Onboarding Date",
      width: 170,
      sortable: true,
      render: (value) => {
        return value ? (
          <Tooltip title={new moment(value).format("DD/MM/YYYY")}>
            <div className="ellipsis-text-container">
              {new moment(value).format("DD/MM/YYYY")}
            </div>
          </Tooltip>
        ) : (
          <span>-</span>
        );
      },
    },
    {
      key: "createdOn",
      label: "Created On/By",
      width: 180,
      sortable: true,
      render: (value, row) => {
        return <span>{LastUpdatedDetails(value, row.createdBy)}</span>;
      },
    },
    {
      key: "updatedOn",
      label: "Last Updated On/By",
      width: 180,
      sortable: true,
      render: (value, row) => {
        return <span>{LastUpdatedDetails(value, row.updatedBy)}</span>;
      },
    },
    {
      key: "status",
      label: "Status",
      pinned: true,
      width: 80,
      sortable: false,
      render: (value) => {
        return value?.trim() ? STATUS_BADGE(value) : <span>-</span>;
      },
    },
    {
      key: "action",
      label: "Action",
      pinned: true,
      width: 80,
      sortable: false,
      render: (value, row) => {
        return (
          <div className="d-flex">
            <Tooltip title="View">
              <span
                onClick={() =>
                  history.push(`/admin/vendorInfo/view/${row?.id}`)
                }
                style={{ cursor: "pointer" }}
                className="ml-2"
              >
                <EyeIcon />
              </span>
            </Tooltip>

            {checkPermission(userPermissions, "U") &&
              ACTIONABLE_STATUSES.includes(row.status?.toUpperCase()) && (
                <Tooltip title="Edit Vendor Details">
                  <span
                    onClick={() =>
                      history.push(`/admin/vendor/create?vendorId=${row?.id}`)
                    }
                    style={{ cursor: "pointer" }}
                    className="ml-2"
                  >
                    <EditIcon />
                  </span>
                </Tooltip>
              )}
          </div>
        );
      },
    },
  ]);

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        flexFlow: "column",
        height: "calc(100vh - 100px)",
      }}
    >
      <Card className={`mb-2 mx-0`} style={{ flexGrow: 1 }}>
        <CardBody style={{ display: "flex", flexFlow: "column" }}>
          <div className="search-match-container d-flex justify-content-between">
            <div
              className={`d-flex align-items-center`}
              style={{ height: "min-content" }}
            >
              <BackIcon
                className="mr-2 large-text"
                onClick={() => history.goBack()}
              />
              <div className="heading-3 color-black-60 text-bold">
                List of vendors
              </div>
            </div>

            <div className="d-flex justify-content-center">
              <AInput
                placeholder="Search"
                className=""
                value={userSearchInput}
                handler={handleOnUserInput}
                onKeyDown={onKeyDownHandler}
                searchIconClickHandler={() => {
                  searchInputHandler();
                }}
                crossIcon={false}
                searchIcon={true}
                style={{ width: "300px" }}
                customSearchIconStyle={{ left: "5px" }}
                rightCustomIconStyles={{
                  fontSize: "12px",
                  position: "absolute",
                  bottom: "7px",
                  right: "115px",
                  margin: "auto",
                }}
                rightCustomIcon={
                  <select
                    value={selected}
                    onChange={(e) => setSelected(e.target.value)}
                    style={{
                      position: "absolute",
                      bottom: "0",
                      margin: "auto 0",
                      background: "#FFF6E3",
                      color: "#A17217",
                      border: "none",
                    }}
                  >
                    {vendorListSearchByOptions.map(({ key, value }) => (
                      <option value={key}>{value}</option>
                    ))}
                  </select>
                }
              />

              {!showAddVendor && (
                <div>
                  <ListFilters
                    filters={filters}
                    setFilters={setFilters}
                    filterCount={filterCount}
                    setFilterCount={setFilterCount}
                    isLoading={loading}
                  />
                  <AButton
                    // variant="outline"
                    // sx={{ marginRight: "10px" }}
                    onClick={handleExportCSV}
                  >
                    <ExcellIcon />
                    Export CSV
                  </AButton>
                </div>
              )}

              {isCreatePermission && (
                <AddVendor
                  showForm={showAddVendor}
                  setForm={setShowAddVendor}
                  onClose={() => setShowAddVendor(false)}
                  onSuccess={(payload) => {
                    /* call API */
                  }}
                  // dropdowns={{ data: { languages, pincodes, buCodes, ... } }}
                />
              )}
            </div>
          </div>

          {loading ? (
            <ALoader />
          ) : (
            !showAddVendor && (
              <div className="pt-3">
                <ServerDataGrid
                  rows={vendorList}
                  columns={columns}
                  rowId={(row) => row.id}
                  loading={loading}
                  pagination={{
                    page: currentPage + 1,
                    pageSize: pageSize,
                    total: totalRows,
                    totalPages: Math.ceil(totalRows / pageSize) || 1,
                  }}
                  onPageChange={(newPage) => setCurrentPage(newPage - 1)}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setCurrentPage(0);
                  }}
                  height="500px"
                />
              </div>
            )
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default VendorsList;
